import axios from 'axios';
import { IMAGE_DOWNLOAD_ERROR_STATUS } from 'modules/integrations/domain/constants';
import { pickTelegramIntegration } from 'modules/integrations/domain/data-mappers';
import {
  CREATE_TOURNAMENT_INTEGRATION_MUTATION,
  DELETE_TOURNAMENT_INTEGRATION_MUTATION,
  TELEGRAM_BOT_INFO_QUERY,
  TOURNAMENT_INTEGRATIONS_QUERY,
} from 'modules/integrations/domain/graphql';
import {
  CreateTelegramIntegrationArgs,
  PublishIntegrationPostArgs,
  PublishIntegrationPostResult,
  TelegramBotInfo,
  TelegramIntegration,
  TournamentIntegrationDto,
} from 'modules/integrations/domain/interfaces';
import { toFetchableStorageUrl } from 'shared/utils/storage-url';
import { baseApi } from 'shared/api/base-api';
import { restBaseQuery } from 'shared/utils/api/rest-base-query';

export const TOURNAMENT_INTEGRATION_TAG = 'TournamentIntegration' as const;

/** Статус, на который бэкенд переводит интеграцию в `DISCONNECTED`: бот потерял доступ к каналу. */
const BOT_LOST_ACCESS_STATUS = 409;

/** Расширение для имени части `images[]` — по mime; без имени php не положит часть в `files`. */
function imageFileName(blob: Blob, index: number): string {
  const extension = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/webp' ? 'webp' : 'png';
  return `post-${index + 1}.${extension}`;
}

/**
 * Картинки скачиваются голым axios, а не `httpResource`: у того глобальный `Authorization`,
 * который незачем отдавать хранилищу (и который превратил бы GET в запрос с preflight).
 */
async function downloadImages(urls: string[], signal: AbortSignal): Promise<Blob[]> {
  return Promise.all(
    urls.map(async (url) => {
      const response = await axios.get<Blob>(toFetchableStorageUrl(url), { responseType: 'blob', signal });
      return response.data;
    }),
  );
}

const tournamentIntegrationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    telegramBotInfo: build.query<TelegramBotInfo, void>({
      query: () => ({ query: TELEGRAM_BOT_INFO_QUERY }),
      transformResponse: (raw: { telegramBotInfo: TelegramBotInfo }) => raw.telegramBotInfo,
    }),

    /** Telegram-интеграция организации или `null`, если канал не подключён. Аргумент — id организации. */
    telegramIntegration: build.query<TelegramIntegration | null, number>({
      query: (organizerId) => ({ query: TOURNAMENT_INTEGRATIONS_QUERY, variables: { tournamentId: organizerId } }),
      transformResponse: (raw: { tournamentIntegrations: TournamentIntegrationDto[] }) =>
        pickTelegramIntegration(raw.tournamentIntegrations ?? []),
      providesTags: (_result, _error, organizerId) => [{ type: TOURNAMENT_INTEGRATION_TAG, id: organizerId }],
    }),

    createTelegramIntegration: build.mutation<TelegramIntegration | null, CreateTelegramIntegrationArgs>({
      query: ({ organizerId, channel }) => ({
        query: CREATE_TOURNAMENT_INTEGRATION_MUTATION,
        variables: { input: { tournamentId: organizerId, type: 'TELEGRAM', telegram: { channel: channel.trim() } } },
      }),
      transformResponse: (raw: { createTournamentIntegration: TournamentIntegrationDto }) =>
        pickTelegramIntegration([raw.createTournamentIntegration]),
      // Отказ (канал не найден, бот не админ) список не меняет — перечитывать его незачем.
      invalidatesTags: (_result, error, { organizerId }) => (error ? [] : [{ type: TOURNAMENT_INTEGRATION_TAG, id: organizerId }]),
    }),

    deleteTournamentIntegration: build.mutation<void, { integrationId: number; organizerId: number }>({
      query: ({ integrationId }) => ({ query: DELETE_TOURNAMENT_INTEGRATION_MUTATION, variables: { id: integrationId } }),
      transformResponse: () => undefined,
      invalidatesTags: (_result, _error, { organizerId }) => [{ type: TOURNAMENT_INTEGRATION_TAG, id: organizerId }],
    }),

    /**
     * Публикация поста — REST `multipart/form-data`, поэтому `queryFn` с `restBaseQuery`, а не
     * GraphQL-`baseQuery` слайса. Живёт в `baseApi`, чтобы ответ 409 мог инвалидировать
     * интеграцию тем же тегом: бэкенд к этому моменту уже перевёл её в `DISCONNECTED`.
     */
    publishIntegrationPost: build.mutation<PublishIntegrationPostResult, PublishIntegrationPostArgs>({
      async queryFn({ integrationId, text, imageUrls }, api, extraOptions) {
        let images: Blob[];
        try {
          images = await downloadImages(imageUrls, api.signal);
        } catch (error) {
          return { error: { status: IMAGE_DOWNLOAD_ERROR_STATUS, message: (error as Error).message } as any };
        }

        const form = new FormData();
        if (text.trim()) form.append('text', text);
        // Content-Type не задаём: axios сам поставит multipart вместе с boundary.
        images.forEach((image, index) => form.append('images[]', image, imageFileName(image, index)));

        const result = await restBaseQuery(
          { url: `/tournament-integrations/${integrationId}/posts`, method: 'POST', data: form },
          api,
          extraOptions,
        );

        if (result.error) return { error: result.error as any };
        return { data: result.data as PublishIntegrationPostResult };
      },
      invalidatesTags: (_result, error) => (error?.status === BOT_LOST_ACCESS_STATUS ? [TOURNAMENT_INTEGRATION_TAG] : []),
    }),
  }),
  overrideExisting: false,
});

export const {
  useTelegramBotInfoQuery,
  useTelegramIntegrationQuery,
  useCreateTelegramIntegrationMutation,
  useDeleteTournamentIntegrationMutation,
  usePublishIntegrationPostMutation,
} = tournamentIntegrationsApi;

export default tournamentIntegrationsApi;
